import { useEffect, useRef } from 'react';
import { versionedUrl } from '../utils/versionReload';

/**
 * Hook to detect when a new version of the app has been deployed
 * and automatically reload the page to prevent white screen issues
 */
export function useVersionCheck() {
  const initialVersionRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    // Function to fetch the current version
    const fetchVersion = async () => {
      try {
        // Add cache-busting timestamp to ensure we get the latest version
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          // If version.json doesn't exist yet, that's okay
          return null;
        }

        const data = await response.json();
        return data.version;
      } catch (error) {
        console.warn('Failed to fetch version:', error);
        return null;
      }
    };

    // Initialize with the current version
    const initVersion = async () => {
      const version = await fetchVersion();
      if (version) {
        initialVersionRef.current = version;
        console.log('App version initialized:', version);
      }
    };

    // Check if version has changed
    const checkVersion = async () => {
      // Don't check if we haven't initialized yet
      if (!initialVersionRef.current) {
        return;
      }

      const currentVersion = await fetchVersion();

      if (currentVersion && currentVersion !== initialVersionRef.current) {
        console.log('New version detected:', currentVersion, '(was:', initialVersionRef.current + ')');
        console.log('Reloading to load new version...');

        // Clear any cached data that might cause issues.
        //
        // Deliberately NOT sessionStorage.clear(), which is what this used to do. Firebase
        // Auth keeps its pending-redirect state there (firebase:pendingRedirect:*), as does
        // authRedirectInFlight in AuthContext, so a version check landing mid-sign-in would
        // wipe the state getRedirectResult is about to look for. A new deploy is exactly
        // when that is most likely — the check runs on every tab that becomes visible.
        try {
          localStorage.removeItem('lastVisit');
        } catch (e) {
          console.warn('Failed to clear storage:', e);
        }

        // Navigate with a cache-busting param to bypass bfcache and force fresh HTML.
        // versionedUrl keeps the visitor's existing params — splitting on '?' discarded them.
        window.location.replace(versionedUrl());
      }
    };

    // Initialize version on mount
    initVersion();

    // Check for new versions every 5 minutes
    checkIntervalRef.current = setInterval(checkVersion, 5 * 60 * 1000);

    // Also check when the page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
